import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'

import { useRive } from '@rive-app/react-canvas'
import { useUnit } from 'effector-react'
import { AnimatePresence, motion } from 'framer-motion'
import { useIsFetching } from 'react-query'
import { useLocation } from 'react-router-dom'

import { AnimatedNumber } from '@/components/ui/animated-number.tsx'
import { $importantLoadingsCount } from '@/models/importantLoadings'
import { useAccount } from '@/modules/accounts/hooks/useAccounts.ts'
import { AppRoutes } from '@/utils/constants.ts'
import { useTheme } from '@/utils/useTheme.tsx'

import LogoAnimation from './logo_animation.riv?url'

const artboardNames = {
  light: 'loader-light',
  dark: 'loader-dark',
}

type LoadingState = 'logging_in' | 'fetching' | 'finish'

const loadingStateMessages = {
  logging_in: 'Logging in...',
  fetching: 'Loading data...',
}

function randomBetween(min: number, max: number) {
  return Math.floor(Math.random() * (max - min + 1) + min)
}

export const LoadingScreen = ({ children }: { children: ReactNode }) => {
  const { isLoggedIn } = useAccount()
  const importantLoadingsCount = useUnit($importantLoadingsCount)
  const queryClientFetchingCount = useIsFetching()

  const isLoading = importantLoadingsCount + queryClientFetchingCount > 0

  const [loadingScreenAnimationState, setLoadingScreenAnimationState] =
    useState<LoadingState>('logging_in')
  const [progress, setProgress] = useState(0)
  const location = useLocation()
  const { styleTheme } = useTheme()

  const { RiveComponent } = useRive({
    src: LogoAnimation,
    stateMachines: 'State Machine 1',
    artboard: styleTheme === 'light' ? artboardNames.light : artboardNames.dark,
    autoplay: true,
  })

  useEffect(() => {
    if (loadingScreenAnimationState === 'finish') {
      return
    }
    if (loadingScreenAnimationState === 'logging_in' && isLoggedIn) {
      const timer = setTimeout(() => {
        setLoadingScreenAnimationState('fetching')
        setProgress(randomBetween(45, 75))
      }, 200)

      return () => clearTimeout(timer)
    }

    if (loadingScreenAnimationState === 'fetching' && !isLoading) {
      setProgress(randomBetween(75, 90))

      const timer = setTimeout(() => {
        setProgress(100)
        setLoadingScreenAnimationState('finish')
      }, 100)

      return () => {
        clearTimeout(timer)
      }
    }
  }, [isLoading, isLoggedIn, loadingScreenAnimationState])

  const accountPagePaths: string[] = [
    AppRoutes.Auth.Register.asUrl(),
    AppRoutes.Auth.RegisterAfterPayment.asUrl(),
    AppRoutes.Auth.Login.asUrl(),
  ]

  const isExcludedPage =
    accountPagePaths.includes(location.pathname) ||
    location.pathname.startsWith(AppRoutes.Auth.ResetPassword.asUrl()) ||
    location.pathname.startsWith(AppRoutes.Auth.ActivateAccount.asUrl()) ||
    location.pathname.startsWith(AppRoutes.Auth.ApplyPromo.asUrl()) ||
    location.pathname.includes('/chatting-ui/')

  return (
    <>
      <AnimatePresence>
        {!isExcludedPage && loadingScreenAnimationState !== 'finish' && (
          <motion.div
            className="pointer-events-none fixed inset-0 z-[71] flex flex-col items-center justify-center gap-4 bg-background"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            data-testid="loading-screen-animation"
          >
            <div className="h-64 w-64">
              <RiveComponent />
            </div>
            <div className="mb-4 h-2 w-64 rounded-full bg-gray-200 dark:bg-gray-800">
              <motion.div
                className="h-full rounded-full bg-blue-500"
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.5 }}
              />
            </div>
            <span className="mb-4 text-title">
              {loadingStateMessages[loadingScreenAnimationState]}
              <span className="ml-3 text-text-sub">
                <AnimatedNumber value={progress} /> %
              </span>
            </span>
          </motion.div>
        )}
      </AnimatePresence>
      {children}
    </>
  )
}
